import {
    Button,
    CheckboxField,
    CprField,
    EmailField,
    ModalBody,
    ModalDialog,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    PhoneField,
    scrollTo,
    Section,
    SectionHeader,
    TextField,
    Tooltip
} from 'flos-react-cvi';
import React from 'react';
import { connect } from 'react-redux';
import { AddressField } from '../components/address-field';
import { useScrollOnMount } from '../hooks/use-scroll-on-mount';
import {
    checkGaeld,
    checkSkader,
    checkSkybrud,
    setCustomerAddress,
    setCustomerCpr,
    setCustomerEmail,
    setCustomerMobile,
    setCustomerName
} from '../store/actions';
import {
    selectCurrentStep,
    selectCustomerAdddressIsValid,
    selectCustomerCprErrorMessage,
    selectCustomerCprErrorType,
    selectCustomerEmailIsValid,
    selectCustomerInfoIsValid,
    selectCustomerInfoValues,
    selectCustomerMobileIsValid,
    selectCustomerNameIsValid,
    selectHasSelectedInsuranceType,
    selectResources,
    selectUiStatus,
    selectDataPolicyContentBlock
} from '../store/selectors';
import { completeCustomerInfo } from '../store/thunks';

const CustomerInfoView = ({
    values: { name, cprNumber, address, mobile, email, skybrudIsChecked, skaderIsChecked, gaeldIsChecked },
    customerNameIsValid,
    cprErrorType,
    cprErrorMsg,
    addressIsValid,
    mobileIsValid,
    emailIsValid,
    setCustomerNameProp,
    setCustomerCprProp,
    setCustomerAddressProp,
    setCustomerMobileProp,
    setCustomerEmailProp,
    checkSkaderProp,
    checkSkybrudProp,
    checkGaeldProp,
    completeSection,
    hasSelectedContentInsurance,
    isFocusOnThisSection,
    isSubmitting,
    customerInfoIsValid,
    resources,
    dataPolicyContentBlock
}) => {
    const scrollTarget = useScrollOnMount();
    const [hasSubmitted, setHasSubmitted] = React.useState(false);
    const formRef = React.useRef();

    React.useEffect(() => {
        if (hasSubmitted && !customerInfoIsValid && formRef.current) {
            const inputWithError = formRef.current.querySelector('.form-group.has-error input');
            if (inputWithError) {
                scrollTo(inputWithError);
            }
        }
    }, [hasSubmitted, customerInfoIsValid]);

    function submit() {
        setHasSubmitted(true);
        completeSection();
    }

    const [showTooOldDialog, dismissTooOldModal] = useTooOldModalState(cprErrorType);

    return (
        <Section variant={isFocusOnThisSection ? 'alt' : undefined} ref={scrollTarget}>
            <div className="container" data-testid="customer-info" ref={formRef}>
                <SectionHeader>{resources.customerInfoSectionHeading}</SectionHeader>
                <div className="row">
                    <div className="col-xs-12 col-md-8 u-Space-up u-Space-down--lg">
                        <div className="row u-Space-down">
                            <div className="col-xs-12 col-sm-7">
                                <TextField
                                    label={resources.customerInfoSectionNameLabel}
                                    value={name}
                                    name="name"
                                    onChangeValue={setCustomerNameProp}
                                    status={hasSubmitted ? (customerNameIsValid ? 'success' : 'error') : null}
                                    errorHelpText={resources.customerInfoSectionNameError}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        <div className="row u-Space-down">
                            <div className="col-xs-12 col-sm-5">
                                <CprField
                                    label={resources.customerInfoSectionCprLabel}
                                    value={cprNumber}
                                    onChangeValue={setCustomerCprProp}
                                    status={hasSubmitted ? (cprErrorMsg ? 'error' : 'success') : null}
                                    errorHelpText={cprErrorMsg}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        <ModalOverlay isOpen={showTooOldDialog} onDismiss={dismissTooOldModal} id="too-old-modal">
                            <ModalDialog>
                                <ModalHeader>
                                    <h4 className="h3">{resources.customerInfoSectionCprOverageModalTitle}</h4>
                                </ModalHeader>
                                <ModalBody>{resources.customerInfoSectionCprOverageModalContent}</ModalBody>
                                <ModalFooter>
                                    <div className="text-left">
                                        <a
                                            href={resources.customerInfoSectionCprOverageModalGetOfferLink}
                                            className="btn btn-primary"
                                            target="_BLANK"
                                            rel="noopener noreferrer"
                                            style={{ marginRight: '20px' }}
                                        >
                                            {resources.customerInfoSectionCprOverageModalGetOfferButtonText}
                                        </a>
                                        <Button onClick={dismissTooOldModal}>
                                            {resources.customerInfoSectionCprOverageModalCloseButtonText}
                                        </Button>
                                    </div>
                                </ModalFooter>
                            </ModalDialog>
                        </ModalOverlay>
                        <div className="row u-Space-down">
                            <div className="col-xs-12 col-sm-7">
                                <AddressField
                                    label={resources.customerInfoSectionAddressLabel}
                                    value={address}
                                    onChangeValue={setCustomerAddressProp}
                                    id="customer-address"
                                    status={hasSubmitted ? (addressIsValid ? 'success' : 'error') : null}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        <div className="row u-Space-down">
                            <div className="col-xs-12 col-sm-5">
                                <PhoneField
                                    label={resources.customerInfoSectionMobileLabel}
                                    value={mobile}
                                    onChangeValue={setCustomerMobileProp}
                                    status={hasSubmitted ? (mobileIsValid ? 'success' : 'error') : null}
                                    errorHelpText={resources.customerInfoSectionMobileError}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        <div className="row u-Space-down">
                            <div className="col-xs-12 col-sm-7">
                                <EmailField
                                    label={resources.customerInfoSectionEmailLabel}
                                    value={email}
                                    id="email"
                                    name="email"
                                    onChangeValue={setCustomerEmailProp}
                                    status={hasSubmitted ? (emailIsValid ? 'success' : 'error') : null}
                                    errorHelpText={resources.customerInfoSectionEmailError}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                    </div>
                    {dataPolicyContentBlock && (
                        <div
                            className="col-xs-12 col-md-4 u-Space-down--lg"
                            dangerouslySetInnerHTML={{ __html: dataPolicyContentBlock }}
                        />
                    )}
                </div>
                {hasSelectedContentInsurance && (
                    <div>
                        <CheckboxField
                            checked={skybrudIsChecked}
                            onChangeValue={checkSkybrudProp}
                            status={hasSubmitted && !skybrudIsChecked && 'error'}
                            label={
                                <span>
                                    <b>{resources.customerInfoSectionConsentSkybrudLabel} </b>
                                    {resources.customerInfoSectionConsentSkybrudDescription}
                                    <Tooltip label={resources.customerInfoSectionConsentSkybrudPopover}>
                                        <flos-icon style={{ margin: '0 5px' }} shape="circle-exclamation" size="20" />
                                    </Tooltip>
                                </span>
                            }
                            required
                        />
                        <CheckboxField
                            checked={skaderIsChecked}
                            onChangeValue={checkSkaderProp}
                            status={hasSubmitted && !skaderIsChecked && 'error'}
                            label={
                                <span>
                                    <b>{resources.customerInfoSectionConsentSkaderLabel} </b>
                                    {resources.customerInfoSectionConsentSkaderDescription}
                                    <Tooltip label={resources.customerInfoSectionConsentSkaderPopover}>
                                        <flos-icon style={{ margin: '0 5px' }} shape="circle-exclamation" size="20" />
                                    </Tooltip>
                                </span>
                            }
                            required
                        />
                        <CheckboxField
                            checked={gaeldIsChecked}
                            onChangeValue={checkGaeldProp}
                            status={hasSubmitted && !gaeldIsChecked && 'error'}
                            label={
                                <span>
                                    <b>{resources.customerInfoSectionConsentGaeldLabel} </b>
                                    {resources.customerInfoSectionConsentGaeldDescription}
                                    <Tooltip label={resources.customerInfoSectionConsentGaeldPopover}>
                                        <flos-icon style={{ margin: '0 5px' }} shape="circle-exclamation" size="20" />
                                    </Tooltip>
                                </span>
                            }
                            required
                        />
                    </div>
                )}
                {isFocusOnThisSection && (
                    <div className="row">
                        <div className="col-xs-12">
                            <Button
                                onClick={submit}
                                isLoading={isSubmitting}
                                disabled={isSubmitting}
                                variant="primary"
                                minWidth="wide"
                            >
                                {resources.customerInfoSectionButtonText}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Section>
    );
};

function useTooOldModalState(cprErrorType) {
    const [showTooOldDialog, setShowTooOldDialog] = React.useState(false);

    React.useEffect(() => {
        if (cprErrorType === 'TOO_OLD') {
            setShowTooOldDialog(true);
        }
    }, [cprErrorType]);

    function dismissTooOldModal() {
        setShowTooOldDialog(false);
    }

    return [showTooOldDialog, dismissTooOldModal];
}

const mapStates = state => ({
    values: selectCustomerInfoValues(state),
    customerNameIsValid: selectCustomerNameIsValid(state),
    cprErrorType: selectCustomerCprErrorType(state),
    cprErrorMsg: selectCustomerCprErrorMessage(state),
    addressIsValid: selectCustomerAdddressIsValid(state),
    mobileIsValid: selectCustomerMobileIsValid(state),
    emailIsValid: selectCustomerEmailIsValid(state),
    resources: selectResources(state),
    dataPolicyContentBlock: selectDataPolicyContentBlock(state),
    hasSelectedContentInsurance: selectHasSelectedInsuranceType(state, 'contents'),
    customerInfoIsValid: selectCustomerInfoIsValid(state),
    isFocusOnThisSection: selectCurrentStep(state) === 3,
    isSubmitting: selectUiStatus(state) === 'submitting'
});

const mapDispatch = dispatch => ({
    setCustomerNameProp: name => dispatch(setCustomerName(name)),
    setCustomerCprProp: cpr => dispatch(setCustomerCpr(cpr)),
    setCustomerAddressProp: address => dispatch(setCustomerAddress(address)),
    setCustomerMobileProp: mobile => dispatch(setCustomerMobile(mobile)),
    setCustomerEmailProp: email => dispatch(setCustomerEmail(email)),
    completeSection: () => dispatch(completeCustomerInfo()),
    checkSkaderProp: checked => dispatch(checkSkader(checked)),
    checkSkybrudProp: checked => dispatch(checkSkybrud(checked)),
    checkGaeldProp: checked => dispatch(checkGaeld(checked))
});

export const CustomerInfo = connect(mapStates, mapDispatch)(CustomerInfoView);
